import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { isNumeric } from 'utils/string';
import { generateUuid } from 'utils/dataUtil';

const SOFTableEditor = React.forwardRef(({ data, columns, onDataChange }, ref) => {
  const tableRef = useRef(null);
  const tabulatorRef = useRef(null);
  const [localData, setLocalData] = useState(data);

  useEffect(() => {
    setLocalData(data);
    if (tabulatorRef.current) {
      tabulatorRef.current.setData(data);
    }
  }, [data]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/tabulator-tables@5.5.2/dist/css/tabulator.min.css';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      .tabulator-container {
        height: 100%;
        display: flex;
        flex-direction: column;
      }
      .tabulator {
        flex: 1;
        max-height: unset !important;
      }
      .tabulator .tabulator-tableholder {
        overflow-y: auto;
        overflow-x: auto;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const addNewRow = () => {
    if (tabulatorRef.current) {
      const newRow = {
        key: generateUuid(),
        SO: '',
        KRO: '',
        PSIA: ''
      };
      tabulatorRef.current.addRow(newRow);
    }
  };

  const deleteSelectedRows = () => {
    if (tabulatorRef.current) {
      const selectedRows = tabulatorRef.current.getSelectedRows();
      selectedRows.forEach(row => row.delete());
      
      const updatedData = tabulatorRef.current.getData();
      setLocalData(updatedData);
      onDataChange(updatedData);
    }
  };

  const handlePaste = useCallback((e) => {
    if (!tabulatorRef.current) return;

    const clipboardData = e.clipboardData.getData('text');
    const rows = clipboardData.split(/\r?\n/).filter(row => row.trim());
    const pastedData = rows.map(row => {
      return row.split('\t').map(cell => cell.trim());
    });
    
    const isSingleColumn = pastedData.every(row => row.length === 1);
    
    if (isSingleColumn) {
      const selectedCell = tabulatorRef.current.getSelectedCells();
      if (!selectedCell || selectedCell.length === 0) {
        alert('Please select a cell before pasting a single column');
        e.preventDefault();
        return;
      }

      const selectedColumn = selectedCell[0].getColumn();
      const selectedField = selectedColumn.getField();
      const selectedRows = selectedCell.map(cell => cell.getRow());

      pastedData.forEach((rowData, index) => {
        if (selectedRows[index]) {
          const value = rowData[0];
          const updateData = {};
          updateData[selectedField] = isNumeric(value) ? parseFloat(value) : value;
          selectedRows[index].update(updateData);
        }
      });
    } else {
      const expectedColumns = columns
        .filter(col => col.key !== 'operation' && col.key !== 'key')
        .length;

      const hasInvalidRows = pastedData.some(row => row.length !== expectedColumns);
      if (hasInvalidRows) {
        alert(`Please paste data with exactly ${expectedColumns} columns (So, Kro, Pc)`);
        e.preventDefault();
        return;
      }

      const columnFields = columns
        .filter(col => col.key !== 'operation' && col.key !== 'key')
        .map(col => col.dataIndex);

      const newRows = pastedData.map((rowData) => {
        const newRow = {
          key: generateUuid()
        };

        columnFields.forEach((field, index) => {
          const value = rowData[index];
          newRow[field] = isNumeric(value) ? parseFloat(value) : value;
        });

        return newRow;
      });
      tabulatorRef.current.addData(newRows);
    }

    e.preventDefault();
  }, [columns]);

  useEffect(() => {
    if (tableRef.current && !tabulatorRef.current) {
      tabulatorRef.current = new Tabulator(tableRef.current, {
        data: data,
        columns: [
          {
            title: "#",
            field: "key",
            formatter: "rownum",
            headerSort: false,
            width: 40,
            frozen: true,
            editor: false
          },
          {
            formatter: "rowSelection", 
            titleFormatter: "rowSelection", 
            hozAlign: "center", 
            headerSort: false, 
            width: 40,
            frozen: true
          },
          ...columns.filter(col => col.key !== 'operation' && col.key !== 'key').map(col => ({
            title: col.title,
            field: col.dataIndex,
            editor: "input",
            validator: ["required", "numeric"],
            clipboard: false
          }))
        ],
        layout: "fitColumns",
        history: true,
        clipboard: false,
        height: "100%",
        selectable: true,
        movableRows: true,
        virtualDomBuffer: 300,
        placeholder: "No Data Available",
        keybindings: {
          "copy": false,
          "paste": false
        },
        dataChanged: function() {
          const updatedData = tabulatorRef.current.getData();
          setLocalData(updatedData);
          onDataChange(updatedData);
        }
      });

      tabulatorRef.current.element.addEventListener('paste', handlePaste);
    }

    return () => {
      if (tabulatorRef.current) {
        tabulatorRef.current.element.removeEventListener('paste', handlePaste);
        tabulatorRef.current.destroy();
        tabulatorRef.current = null;
      }
    };
  }, [columns, handlePaste]);

  React.useImperativeHandle(ref, () => ({
    getCurrentData: () => {
      return tabulatorRef.current ? tabulatorRef.current.getData() : localData;
    }
  }));

  return (
    <div className="tabulator-container" style={{ height: '100%' }}>
      <div style={{ marginBottom: '10px' }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={addNewRow}
          style={{ marginRight: '8px' }}
        >
          Add Row
        </Button>
        <Button 
          danger 
          icon={<DeleteOutlined />} 
          onClick={deleteSelectedRows}
        >
          Delete Selected
        </Button>
      </div>
      <div ref={tableRef} style={{ flex: 1 }} />
    </div>
  );
});

export default SOFTableEditor; 